import type { Freshness, TalosResponse } from '@pulse/contracts';
import * as React from 'react';

import { AttentionItem } from '@/components/pulse/attention-item';
import { BuiltThisWeek } from '@/components/pulse/built-this-week';
import { AsOfPill } from '@/components/pulse/day';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { NextList, WorkloadBar, WorkloadChips } from '@/components/pulse/workload';
import { useShell } from '@/domains/shell/api';
import { useTalos } from '@/domains/talos/api';
import { CapacityCard } from '@/domains/talos/capacity-card';

/** "12a", "3a" … "9p": every third hour labelled, as the prototype's hour axis. */
function hourTick(hour: number): string {
  if (hour % 3 !== 0) return '';
  return hour === 0 ? '12a' : hour < 12 ? `${hour}a` : hour === 12 ? '12p' : `${hour - 12}p`;
}

/** The Talos page (TLS-1 to TLS-10), built from the prototype's `viewTalos()`. An as-of page. */
export function TalosPage(): React.ReactElement {
  const page = useTalos();
  const shell = useShell();
  const status = page.data?.status ?? null;
  return (
    <>
      <PageHeader
        eyebrow="Agent · Talos"
        title="Talos"
        sub="The agent Mac mini: its health, its day and its queue, from the Mini's reports."
        right={
          <div className="row" style={{ gap: 12 }}>
            {status !== null ? (
              <span className="row" style={{ gap: 8, fontWeight: 500, color: status === 'Stopped' ? 'var(--bad-ink)' : 'var(--accent-ink)' }}>
                <span className={`status-dot${status === 'Stopped' ? ' off' : ''}`} />
                {status}
              </span>
            ) : null}
            <AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />
          </div>
        }
      />
      {page.data !== undefined ? <TalosView data={page.data} freshness={shell.data?.freshness ?? null} capacity={<CapacityCard />} /> : null}
    </>
  );
}

export function TalosView({ data, freshness, capacity = null }: { readonly data: TalosResponse; readonly freshness: Freshness | null; readonly capacity?: React.ReactNode }): React.ReactElement {
  const peak = Math.max(0, ...data.byHour);
  const top = Math.max(5, Math.ceil(peak / 5) * 5);
  const roleTop = Math.max(1, ...data.byRole.map((role) => role.count));
  return (
    <>
      <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="hh">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="hh">Health</h2>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{data.checkedLabel === null ? "The Mini hasn't reported yet." : `${data.checkedLabel} Warns if no run for 90 minutes.`}</span>
        </div>
        {data.health.length > 0 ? (
          <div className="health">
            {data.health.map((cell) => (
              <div key={cell.label} className={`hcell${cell.warn ? ' warn' : ''}`}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{cell.label}</span>
                <span style={{ fontWeight: 600, color: cell.warn ? 'var(--warn-ink)' : 'var(--ink)' }}>{cell.value}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{cell.detail}</span>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: 'var(--ink-2)' }}>Talos&apos;s run log and health arrive with COL-010.</span>
        )}
      </section>
      {capacity}
      <Kpis items={data.tiles.map((tile) => ({ label: tile.label, value: tile.value, sub: tile.sub, warn: tile.warn }))} />
      {data.person !== null ? <BuiltThisWeek thisWeek={data.person.thisWeek} freshness={freshness} /> : null}
      {/* One grid: the right column stacks its cards, so no row leaves a gap beside a tall card (Sumit, 2026-09-27). */}
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px', alignItems: 'start' }}>
        <div className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2>Queue: next up</h2>
            {data.queue.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing ready for development is assigned to Talos.</span> : <NextList items={data.queue} />}
          </section>
          <section className="card pad stack" style={{ gap: 14 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <h2>Work items by hour</h2>
              <span className="sub" style={{ fontSize: 13 }}>
                Eastern time{data.busiestHour === null ? '' : ` · busiest ${data.busiestHour}`}
              </span>
            </div>
            <div className="chart" role="img" aria-label={`Work items per hour, Eastern time: ${data.byHour.map((count, hour) => `${hourTick(hour) || hour} ${count}`).join(', ')}`}>
              <div className="yaxis num">
                {[top, (top * 3) / 4, top / 2, top / 4, 0].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
              <div className="plot">
                {data.byHour.map((count, hour) => (
                  <i key={hour} className={count === peak && peak > 0 ? 'peak' : undefined} style={{ height: `${((count / top) * 100).toFixed(1)}%` }} title={`${String(hour).padStart(2, '0')}:00 · ${count} items`} />
                ))}
              </div>
            </div>
            <div className="xaxis">
              {data.byHour.map((_, hour) => (
                <span key={hour}>{hourTick(hour)}</span>
              ))}
            </div>
          </section>
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          {data.person !== null ? (
            <section className="card pad stack" style={{ gap: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h2>Assigned to Talos</h2>
                <span className="num" style={{ fontSize: 22, fontWeight: 600 }}>
                  {data.person.workload.total}
                </span>
              </div>
              <WorkloadBar summary={data.person.workload} />
              <WorkloadChips summary={data.person.workload} />
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>{data.person.workload.sources}</span>
            </section>
          ) : null}
          <section className="card pad stack" style={{ gap: 12 }}>
            <h2>By role</h2>
            {data.byRole.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No work items today.</span> : null}
            {data.byRole.map((role) => (
              <div key={role.role} className="stack" style={{ gap: 6 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span>{role.role}</span>
                  <b className="num">{role.count}</b>
                </div>
                <div className="meter">
                  <i style={{ width: `${((role.count / roleTop) * 100).toFixed(0)}%` }} />
                </div>
              </div>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Anything that failed</h2>
            {data.failures.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing failed today.</span> : null}
            {data.failures.map((row, index) => (
              <AttentionItem key={`${row.ref}${index}`} row={row} />
            ))}
          </section>
        </aside>
      </div>
      <section className="card pad stack" style={{ gap: 12 }}>
        <h2>Waiting on a person</h2>
        {data.waiting.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>None of Talos&apos;s PRs waits on a person.</span> : null}
        <div className="projects" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
          {data.waiting.map((row) => (
            <AttentionItem key={row.url} row={row} />
          ))}
        </div>
      </section>
    </>
  );
}
